import { useState } from "react";
import { toast } from "sonner";
import { p } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Send, MessageCircle, Smartphone, Copy, Mail, KeyRound } from "lucide-react";

/**
 * Un solo gesto per far entrare una persona: PIN nuovo, link del locale e messaggio già scritto.
 * Su WhatsApp va tutto insieme; per i tablet senza WhatsApp l'email porta solo il link.
 */
export const MandaAccesso = ({ s, onDone }) => {
  const [apri, setApri] = useState(false);
  const [d, setD] = useState(null);
  const [mail, setMail] = useState("");
  const [busy, setBusy] = useState(false);

  const prepara = async () => {
    setBusy(true);
    try {
      const r = await p(`/staff/${s.id}/accesso`);
      setD(r);
      setMail(r.email || "");
      setApri(true);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accesso non preparato: riprova");
    } finally {
      setBusy(false);
    }
  };

  const mandaMail = async () => {
    try {
      const r = await p(`/staff/${s.id}/accesso-email`, { email: mail });
      toast.success(`Link inviato a ${r.to}`, {
        description: "Nell'email c'è solo il link: il PIN glielo dici tu a voce.",
        duration: 12000,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email non inviata");
    }
  };

  return (
    <>
      <Button size="sm" onClick={prepara} disabled={busy} data-testid={`staff-accesso-${s.id}`}>
        <Send className="mr-1.5 h-3.5 w-3.5" /> Manda l'accesso
      </Button>

      <Dialog open={apri} onOpenChange={setApri}>
        <DialogContent className="max-w-lg" data-testid="accesso-dialog">
          <DialogTitle className="text-lg font-bold">Accesso di {d?.name}</DialogTitle>
          {d && (
            <div className="space-y-4">
              <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-800">
                  PIN nuovo — il vecchio non funziona più
                </p>
                <p className="font-num mt-1 text-4xl font-bold tracking-[0.2em] text-amber-950" data-testid="accesso-pin">
                  {d.pin}
                </p>
              </div>

              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  Messaggio già scritto
                </p>
                <p className="mt-1 whitespace-pre-line text-sm text-slate-700" data-testid="accesso-messaggio">
                  {d.message}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button asChild data-testid="accesso-whatsapp">
                  <a href={`https://wa.me/?text=${encodeURIComponent(d.message)}`} target="_blank" rel="noreferrer">
                    <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
                  </a>
                </Button>
                <Button asChild variant="outline" data-testid="accesso-sms">
                  <a href={`sms:?&body=${encodeURIComponent(d.message)}`}>
                    <Smartphone className="mr-1.5 h-4 w-4" /> SMS
                  </a>
                </Button>
                <Button
                  variant="outline"
                  data-testid="accesso-copia"
                  onClick={() => {
                    navigator.clipboard?.writeText(d.message);
                    toast.success("Messaggio copiato: incollalo dove vuoi");
                  }}
                >
                  <Copy className="mr-1.5 h-4 w-4" /> Copia
                </Button>
              </div>

              <div className="space-y-2 border-t pt-3">
                <p className="text-[11px] text-slate-500">
                  Tablet o telefono senza WhatsApp? Manda l'email con il link: il PIN qui sopra
                  glielo dici tu a voce, così non resta scritto nella sua casella.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Input
                    className="h-10 w-56"
                    type="email"
                    placeholder="email della persona"
                    value={mail}
                    onChange={(e) => setMail(e.target.value)}
                    data-testid="accesso-email-input"
                  />
                  <Button variant="outline" onClick={mandaMail} disabled={!mail} data-testid="accesso-email-btn">
                    <Mail className="mr-1.5 h-4 w-4" /> Manda il link
                  </Button>
                </div>
              </div>

              <p className="flex items-start gap-1.5 text-[11px] text-slate-500">
                <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Il PIN si vede solo adesso: non viene conservato in chiaro da nessuna parte. Se lo
                perde, premi di nuovo «Manda l'accesso» e ne arriva uno nuovo.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
